import { Box, useMediaQuery, useTheme } from '@mui/material';
import { MultipleSelectField } from '@resurs-internal/mui/components/MultipleSelectfield/MultipleSelectField';
import { SelectFieldOption } from '@resurs-internal/mui/components/SelectField/SelectFieldOption';
import { useEffect, useMemo } from 'react';

type Props = {
    name: string;
    label?: string;
    value?: string | string[];
    options: SelectFieldOption[];
    onChange: (value: string[]) => void;
};

const CountryCodeSelection = ({ name, label, options, value, onChange }: Props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const filteredOptions = useMemo(
        () => options.filter((option) => option.id != null && option.id !== ''),
        [options],
    );

    const normalizedValue = useMemo(() => {
        const rawValues: string[] = Array.isArray(value)
            ? value
            : typeof value === 'string' && value.length
              ? [value]
              : [];

        if (!rawValues.length) return [];

        return rawValues
            .map((val) => {
                const byId = filteredOptions.find((option) => option.id === val);
                if (byId?.id != null) {
                    return String(byId.id);
                }
                const byLabel = filteredOptions.find((option) => option.label === val && option.id != null);
                if (byLabel) {
                    return String(byLabel.id);
                }
                return null;
            })
            .filter((id): id is string => id !== null);
    }, [value, filteredOptions]);

    useEffect(() => {
        if (!normalizedValue.length && filteredOptions.length === 1) {
            onChange([String(filteredOptions[0].id)]);
        }
    }, [filteredOptions, normalizedValue.length, onChange]);

    return (
        <Box data-testid="country-code-selector">
            <MultipleSelectField
                name={name}
                label={label}
                outline="single"
                options={filteredOptions}
                value={normalizedValue}
                onChange={onChange}
                isMobile={isMobile}
                fullWidth
                disabled={!filteredOptions.length}
            />
        </Box>
    );
};

export default CountryCodeSelection;
